/**
 * LoreCardModal — full-view modal for Archive lore entries.
 *
 * Two states:
 *  • Unlocked: name (tier colour), full-size visual-canon image if
 *    available, Tinkerpug's full excerpt (untruncated), tier badge +
 *    rank requirement, filed date, close.
 *  • Locked: tier header, lock glyph, the teaser, and an "Open the
 *    Archive" button that puts the entry's prompt into the chat input.
 *
 * Esc closes. Click-outside-content closes. Focus is trapped to the
 * primary action on open.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Lock, Sparkles, MessageCircle, PawPrint, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER = {
  1: { color: "#00FFA3", label: "TIER I", rank: "Seeker" },
  2: { color: "#B47CFF", label: "TIER II", rank: "Archivist" },
  3: { color: "#F5D300", label: "TIER III", rank: "Keeper's Circle" },
  special: { color: "#F5D300", label: "ARCHIVE SPECIAL" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function LoreCardModal({ entry, onClose, onOpenArchive }) {
  const primaryBtnRef = useRef(null);
  const tierMeta = TIER[entry?.tier] || TIER[1];
  const unlocked = entry ? !entry.locked : false;
  const [image, setImage] = useState(null);

  // Lazy-fetch the full-size visual canon image for unlocked entries.
  useEffect(() => {
    if (!unlocked || !entry?.has_image) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(entry.imageUrl);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data.image_base64) return;
        setImage(`data:${data.image_mime || "image/png"};base64,${data.image_base64}`);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unlocked, entry?.has_image, entry?.id]);

  // Esc to close + focus primary button
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    primaryBtnRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!entry) return null;

  const handleOpenArchive = () => {
    onClose?.();
    onOpenArchive?.(entry.prompt || "");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="lore-card-modal"
      onClick={onClose}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 backdrop-blur-[6px]"
      style={{ background: "rgba(3,5,14,0.82)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
        style={{
          background: "linear-gradient(180deg, rgba(10,15,30,0.98) 0%, rgba(5,7,18,0.98) 100%)",
          border: `1px solid ${tierMeta.color}44`,
          boxShadow: `0 0 48px ${tierMeta.color}22, inset 0 0 0 1px rgba(255,255,255,0.03)`,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="lore-card-modal-close"
          className="absolute top-3 right-3 rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition-colors z-10"
        >
          <X size={16} />
        </button>

        {unlocked ? (
          <div className="p-6 sm:p-8" data-testid="lore-card-modal-unlocked">
            <div className="flex items-center gap-2 mb-3">
              {entry.tier === "special" ? (
                <PawPrint size={14} style={{ color: tierMeta.color }} strokeWidth={2.4} />
              ) : (
                Array.from({ length: entry.tier }).map((_, i) => (
                  <span
                    key={i}
                    className="rounded-full"
                    style={{ width: 7, height: 7, background: tierMeta.color, boxShadow: `0 0 8px ${tierMeta.color}` }}
                  />
                ))
              )}
              <span
                className="text-[10px] font-bold uppercase tracking-[0.32em] ml-1"
                style={{ color: tierMeta.color, fontFamily: "Orbitron, sans-serif" }}
              >
                {entry.tier === "special" ? `${tierMeta.label} · ${entry.name}` : tierMeta.label}
              </span>
              <Sparkles size={13} style={{ color: tierMeta.color, marginLeft: "auto" }} />
            </div>
            <h2
              className="text-2xl sm:text-3xl font-bold leading-tight mb-4"
              style={{ color: tierMeta.color, fontFamily: "Orbitron, sans-serif", letterSpacing: "0.01em" }}
              data-testid="lore-card-modal-title"
            >
              {entry.name}
            </h2>
            {image && (
              <div
                className="relative w-full rounded-xl overflow-hidden mb-5"
                style={{ border: `1px solid ${tierMeta.color}33`, boxShadow: `0 0 20px ${tierMeta.color}22` }}
              >
                <img
                  src={image}
                  alt={entry.name}
                  className="w-full h-auto max-h-[420px] object-cover"
                  draggable={false}
                />
              </div>
            )}
            {entry.excerpt && (
              <blockquote
                className="text-sm leading-relaxed text-slate-200 whitespace-pre-wrap border-l-2 pl-4 py-1"
                style={{ borderColor: `${tierMeta.color}66` }}
                data-testid="lore-card-modal-excerpt"
              >
                {entry.excerpt.replace(/^["“]|["”]$/g, "")}
              </blockquote>
            )}
            {entry.unlocked_at && (
              <p
                className="mt-5 text-[10px] uppercase tracking-[0.28em] text-slate-500"
                style={{ fontFamily: "monospace" }}
                data-testid="lore-card-modal-filed"
              >
                Filed · {formatDate(entry.unlocked_at)}
              </p>
            )}

            {/* The special tile reveals the /shop waitlist
                as the next step once unlocked. This CTA only appears
                on the special entry — every other entry stops at the
                filed date. */}
            {entry.tier === "special" && (
              <div className="mt-6 pt-5 border-t border-white/[0.06]">
                <Link
                  to="/shop"
                  onClick={onClose}
                  data-testid="lore-card-modal-kennel-cta"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold uppercase tracking-widest text-[11px] transition-all hover:scale-[1.02]"
                  style={{
                    background: "#F5D300",
                    color: "#0a0a12",
                    fontFamily: "Orbitron, sans-serif",
                    boxShadow: "0 0 20px rgba(245,211,0,0.35)",
                  }}
                >
                  The Kennel is waiting <ArrowRight size={13} />
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 sm:p-8" data-testid="lore-card-modal-locked">
            <div className="flex items-center gap-2 mb-6">
              {entry.tier === "special" ? (
                <PawPrint size={14} className="text-slate-500" strokeWidth={2.4} />
              ) : (
                Array.from({ length: entry.tier }).map((_, i) => (
                  <span
                    key={i}
                    className="rounded-full"
                    style={{ width: 7, height: 7, background: "rgba(255,255,255,0.25)" }}
                  />
                ))
              )}
              <span
                className="text-[10px] font-bold uppercase tracking-[0.32em] ml-1 text-slate-400"
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                {tierMeta.label} · Sealed
              </span>
              <Lock size={13} className="text-slate-500 ml-auto" />
            </div>

            {/* Large lock glyph — replaces the previously-shown pull-copy
                description. The description was leaking as selectable
                text, which let visitors paste it into the chat to shortcut
                the unlock flow. Locked cards must reveal NOTHING that can
                be copied out. */}
            <div className="flex justify-center mb-6" aria-hidden="true">
              <div
                className="rounded-full p-6"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Lock size={40} className="text-slate-500" strokeWidth={1.4} />
              </div>
            </div>

            <p
              className="text-sm text-slate-400 mb-6 text-center"
              data-testid="lore-card-modal-locked-prompt"
            >
              {entry.teaser}
            </p>
            <div className="flex justify-center">
              <button
                ref={primaryBtnRef}
                type="button"
                onClick={handleOpenArchive}
                data-testid="lore-card-modal-open-archive"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold uppercase tracking-widest text-[11px] transition-all"
                style={{
                  background: tierMeta.color,
                  color: "#000",
                  fontFamily: "Orbitron, sans-serif",
                  boxShadow: `0 0 20px ${tierMeta.color}55`,
                }}
              >
                <MessageCircle size={13} />
                Open the Archive
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
